import React, { useEffect, useMemo, useState } from "react";
import { Alert, Badge, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, IconButton, MenuItem, Select, Stack, Switch, TextField, Typography } from "@mui/material";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import AddCircleRoundedIcon from "@mui/icons-material/AddCircleRounded";
import NotificationsRoundedIcon from "@mui/icons-material/NotificationsRounded";
import PageFrame from "./PageFrame";
import api from "../bootstrapApi";
import DirectoryRowCard from "../components/DirectoryRowCard";
import {
  saasCircleCountSx,
  saasDirectoryWrapSx,
  saasHeroActionButtonSx,
  saasInputSx,
  saasSectionCardSx,
  saasSoftCountSx,
} from "../components/saasStyles";

const initialProductForm = {
  categoryId: "",
  name: "",
  description: "",
  price: "",
  currency: "EUR",
  productType: "standard",
  source: "local",
  imageUrl: "",
};

const initialEditForm = {
  categoryId: "",
  name: "",
  description: "",
  price: "",
  currency: "EUR",
  productType: "standard",
  source: "local",
  imageUrl: "",
  active: true,
  stockManaged: true,
};

export default function ProductsPage() {
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productForm, setProductForm] = useState(initialProductForm);
  const [editForm, setEditForm] = useState(initialEditForm);
  const [message, setMessage] = useState(null);
  const [search, setSearch] = useState("");
  const [selectedProductId, setSelectedProductId] = useState(null);
  const [quickAction, setQuickAction] = useState("default");
  const [productOpen, setProductOpen] = useState(false);

  const loadCatalog = async () => {
    const [categoriesResponse, productsResponse] = await Promise.all([api.get("/api/categories"), api.get("/api/products")]);
    setCategories(categoriesResponse.data);
    setProducts(productsResponse.data);
  };

  const loadProductDetail = async (id) => {
    const response = await api.get(`/api/products/${id}`);
    setSelectedProduct(response.data);
    setEditForm({
      categoryId: String(response.data.category_id || ""),
      name: response.data.name || "",
      description: response.data.description || "",
      price: response.data.price || "",
      currency: response.data.currency || "EUR",
      productType: response.data.product_type || "standard",
      source: response.data.source || "local",
      imageUrl: response.data.primary_image || "",
      active: Boolean(response.data.active),
      stockManaged: Boolean(response.data.stock_managed),
    });
  };

  useEffect(() => {
    loadCatalog().catch(() => {
      setMessage({ type: "error", text: "Le catalogue n'a pas pu etre charge." });
    });
  }, []);

  useEffect(() => {
    if (!selectedProductId) {
      setSelectedProduct(null);
      setEditForm(initialEditForm);
      return;
    }

    loadProductDetail(selectedProductId).catch(() => setMessage({ type: "error", text: "Les details du produit n'ont pas pu etre charges." }));
  }, [selectedProductId]);

  const filteredProducts = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const base = !needle
      ? products
      : products.filter((product) =>
          [product.name, product.description, product.category_name, product.product_type, product.source]
            .filter(Boolean)
            .join(" ")
            .toLowerCase()
            .includes(needle)
        );

    if (quickAction === "standard") {
      return base.filter((product) => product.product_type === "standard");
    }
    if (quickAction === "custom") {
      return base.filter((product) => product.product_type === "custom");
    }
    if (quickAction === "odoo") {
      return base.filter((product) => product.source === "odoo");
    }
    return base;
  }, [products, search, quickAction]);

  const handleProductSubmit = async () => {
    try {
      await api.post("/api/products", {
        ...productForm,
        price: Number(productForm.price),
        categoryId: Number(productForm.categoryId),
      });
      setProductForm(initialProductForm);
      await loadCatalog();
      setProductOpen(false);
      setMessage({ type: "success", text: "Le produit a ete cree." });
    } catch (_error) {
      setMessage({ type: "error", text: "Le produit n'a pas pu etre enregistre." });
    }
  };

  const handleSaveProduct = async () => {
    if (!selectedProductId) {
      return;
    }

    try {
      const response = await api.patch(`/api/products/${selectedProductId}`, {
        ...editForm,
        categoryId: Number(editForm.categoryId),
        price: Number(editForm.price),
      });
      await loadCatalog();
      await loadProductDetail(selectedProductId);
      setSelectedProduct(response.data);
      setMessage({ type: "success", text: "Le produit a ete mis a jour." });
    } catch (_error) {
      setMessage({ type: "error", text: "Le produit n'a pas pu etre enregistre." });
    }
  };

  return (
    <PageFrame
      eyebrow="SmartDoctors"
      title="Catalogue produits"
      description="Vue des produits, des images, des prix et des actions du catalogue."
      stats={[]}
      action={
        <Button variant="contained" startIcon={<AddCircleRoundedIcon />} onClick={() => setProductOpen(true)} sx={saasHeroActionButtonSx}>
          + Nouveau produit
        </Button>
      }
    >
      {message ? <Alert severity={message.type}>{message.text}</Alert> : null}

      <Box sx={{ ...saasDirectoryWrapSx, borderRadius: "44px", px: { xs: 2, md: 2.5 }, py: { xs: 1.2, md: 1.4 } }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.25 }}>
          Afficher {filteredProducts.length} elements
        </Typography>

        <Stack direction={{ xs: "column", xl: "row" }} spacing={1.25} alignItems={{ xs: "stretch", xl: "center" }}>
          <TextField
            label="Rechercher des produits"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            sx={{
              ...saasInputSx,
              flex: 1,
              "& .MuiOutlinedInput-root": {
                borderRadius: "18px",
                minHeight: 44,
                backgroundColor: "#fff",
                boxShadow: "none",
              },
            }}
            InputProps={{
              startAdornment: <SearchRoundedIcon sx={{ color: "#5f6fff", mr: 1 }} />,
            }}
          />

          <FormControl sx={{ minWidth: { xs: "100%", xl: 350 } }}>
            <Select value={quickAction} onChange={(event) => setQuickAction(event.target.value)} displayEmpty sx={{ minHeight: 56, borderRadius: "18px", bgcolor: "#fff", fontWeight: 700, boxShadow: "none" }}>
              <MenuItem value="default">Action rapide</MenuItem>
              <MenuItem value="standard">Produits standards</MenuItem>
              <MenuItem value="custom">Produits sur mesure</MenuItem>
              <MenuItem value="odoo">Importes d'Odoo</MenuItem>
            </Select>
          </FormControl>

          <Box sx={{ ...saasSoftCountSx, minWidth: 106, minHeight: 44, bgcolor: "#f5f7ff" }}>
            <Typography variant="body2" sx={{ color: "#5867ff", fontWeight: 700 }}>
              {categories.length} categories
            </Typography>
          </Box>

          <Box sx={{ width: 58, height: 44, borderRadius: "16px", border: "1px solid #dfe6f4", bgcolor: "#fff", display: "grid", placeItems: "center" }}>
            <Badge badgeContent={products.filter((product) => product.source === "odoo").length} color="error">
              <IconButton size="small" sx={{ color: "#5a68ff" }}>
                <NotificationsRoundedIcon />
              </IconButton>
            </Badge>
          </Box>
        </Stack>
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", xl: "1.1fr 0.9fr" }, gap: 3 }}>
        <Box sx={{ ...saasSectionCardSx, borderRadius: "44px", display: "grid", gap: 2.25, px: { xs: 2, md: 2.4 }, py: { xs: 1.4, md: 1.8 } }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, alignItems: "flex-start", flexWrap: "wrap" }}>
            <Box>
              <Typography variant="h6" sx={{ fontSize: 18, mb: 0.3 }}>Produits</Typography>
              <Typography variant="body2" color="text.secondary">
                Vue structuree avec categorie, type de produit et visibilite du prix.
              </Typography>
            </Box>

            <Stack direction="row" spacing={1.25} alignItems="center">
              <Box sx={saasCircleCountSx}><Box><Typography variant="caption" sx={{ color: "#7d89a2" }}>Visibles</Typography><Typography variant="h6" sx={{ color: "#25324b", textAlign: "center" }}>{filteredProducts.length}</Typography></Box></Box>
              <Box sx={saasCircleCountSx}><Box><Typography variant="caption" sx={{ color: "#7d89a2" }}>Selection</Typography><Typography variant="h6" sx={{ color: "#25324b", textAlign: "center" }}>{selectedProductId ? 1 : 0}</Typography></Box></Box>
            </Stack>
          </Box>

          {filteredProducts.map((product) => (
            <DirectoryRowCard
              key={product.id}
              selected={selectedProductId === product.id}
              avatarText={(product.name || "P").slice(0, 1).toUpperCase()}
              title={product.name}
              subtitle={`PR-${product.id}`}
              chips={[
                { label: product.product_type === "standard" ? "Standard" : "Sur mesure", color: product.product_type === "standard" ? "success" : "default" },
                { label: product.source, color: "default" },
              ]}
              columns={[
                { label: "Categorie", value: product.category_name || "-", secondary: product.description || "Aucune description" },
                { label: "Tarification", value: `${Number(product.price || 0).toFixed(2)} ${product.currency || "EUR"}`, secondary: product.primary_image ? "Image liee" : "Aucune image" },
              ]}
              actionLabel={selectedProductId === product.id ? "Selectionne" : "Choisir"}
              onSelect={() => setSelectedProductId(product.id)}
            />
          ))}
        </Box>

        <Box sx={{ ...saasSectionCardSx, borderRadius: "44px", display: "grid", gap: 2, px: { xs: 2, md: 2.4 }, py: { xs: 1.4, md: 1.8 }, alignContent: "start" }}>
          <Box>
            <Typography variant="h6" sx={{ fontSize: 18, mb: 0.3 }}>Espace produit</Typography>
            <Typography variant="body2" color="text.secondary">Gestion de la categorie, de l'image, du prix et du statut actif.</Typography>
          </Box>

          {!selectedProduct ? (
            <Typography variant="body2" color="text.secondary">Choisissez un produit a gauche pour le modifier.</Typography>
          ) : (
            <>
              <TextField select label="Categorie" value={editForm.categoryId} onChange={(event) => setEditForm((c) => ({ ...c, categoryId: event.target.value }))} sx={saasInputSx}>
                {categories.map((category) => (
                  <MenuItem key={category.id} value={category.id}>{category.name}</MenuItem>
                ))}
              </TextField>
              <TextField label="Nom" value={editForm.name} onChange={(event) => setEditForm((c) => ({ ...c, name: event.target.value }))} sx={saasInputSx} />
              <TextField label="Prix" type="number" value={editForm.price} onChange={(event) => setEditForm((c) => ({ ...c, price: event.target.value }))} sx={saasInputSx} />
              <TextField select label="Type" value={editForm.productType} onChange={(event) => setEditForm((c) => ({ ...c, productType: event.target.value }))} sx={saasInputSx}>
                <MenuItem value="standard">standard</MenuItem>
                <MenuItem value="custom">sur mesure</MenuItem>
              </TextField>
              <TextField select label="Source" value={editForm.source} onChange={(event) => setEditForm((c) => ({ ...c, source: event.target.value }))} sx={saasInputSx}>
                <MenuItem value="local">locale</MenuItem>
                <MenuItem value="odoo">odoo</MenuItem>
              </TextField>
              <TextField label="Description" multiline minRows={3} value={editForm.description} onChange={(event) => setEditForm((c) => ({ ...c, description: event.target.value }))} sx={saasInputSx} />
              <TextField label="URL image" value={editForm.imageUrl} onChange={(event) => setEditForm((c) => ({ ...c, imageUrl: event.target.value }))} sx={saasInputSx} />
              <Stack direction="row" spacing={2} alignItems="center">
                <Typography variant="body2" sx={{ color: "#25324b", fontWeight: 700 }}>Actif</Typography>
                <Switch checked={editForm.active} onChange={(event) => setEditForm((c) => ({ ...c, active: event.target.checked }))} />
                <Typography variant="body2" sx={{ color: "#25324b", fontWeight: 700 }}>Gere en stock</Typography>
                <Switch checked={editForm.stockManaged} onChange={(event) => setEditForm((c) => ({ ...c, stockManaged: event.target.checked }))} />
              </Stack>
              <Button variant="contained" onClick={handleSaveProduct} sx={{ borderRadius: "14px" }}>Enregistrer le produit</Button>
            </>
          )}
        </Box>
      </Box>

      <Dialog open={productOpen} onClose={() => setProductOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Creer un produit</DialogTitle>
        <DialogContent sx={{ display: "grid", gap: 2, pt: 1.5 }}>
          <TextField select label="Categorie" value={productForm.categoryId} onChange={(event) => setProductForm((current) => ({ ...current, categoryId: event.target.value }))} sx={saasInputSx}>
            {categories.map((category) => (
              <MenuItem key={category.id} value={category.id}>{category.name}</MenuItem>
            ))}
          </TextField>
          <TextField label="Nom" value={productForm.name} onChange={(event) => setProductForm((current) => ({ ...current, name: event.target.value }))} sx={saasInputSx} />
          <TextField label="Prix" type="number" value={productForm.price} onChange={(event) => setProductForm((current) => ({ ...current, price: event.target.value }))} sx={saasInputSx} />
          <TextField select label="Type" value={productForm.productType} onChange={(event) => setProductForm((current) => ({ ...current, productType: event.target.value }))} sx={saasInputSx}>
            <MenuItem value="standard">standard</MenuItem>
            <MenuItem value="custom">sur mesure</MenuItem>
          </TextField>
          <TextField select label="Source" value={productForm.source} onChange={(event) => setProductForm((current) => ({ ...current, source: event.target.value }))} sx={saasInputSx}>
            <MenuItem value="local">locale</MenuItem>
            <MenuItem value="odoo">odoo</MenuItem>
          </TextField>
          <TextField label="Description" multiline minRows={3} value={productForm.description} onChange={(event) => setProductForm((current) => ({ ...current, description: event.target.value }))} sx={saasInputSx} />
          <TextField label="URL image" value={productForm.imageUrl} onChange={(event) => setProductForm((current) => ({ ...current, imageUrl: event.target.value }))} sx={saasInputSx} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProductOpen(false)}>Annuler</Button>
          <Button variant="contained" onClick={handleProductSubmit} disabled={!productForm.categoryId || !productForm.name || !productForm.price}>Enregistrer le produit</Button>
        </DialogActions>
      </Dialog>

    </PageFrame>
  );
}
